import { collection, getDocs, limit, query } from 'firebase/firestore';
import { auth, db } from '../config/firebaseConfig';

/**
 * "İlk giriş" karşılama mesajı tespiti.
 *
 * Veritabanında `firstLogin` benzeri bir alan bulunmadığı için, mevcut
 * verilerden yararlanan üç kademeli bir kontrol uygulanır. Üç koşul da
 * sağlanmadıkça karşılama mesajı gösterilmez:
 *
 *   1. Hesap kesim tarihinden sonra oluşturulmuş olmalı
 *      (Firebase Auth `metadata.creationTime`). Bu, bu özellik devreye
 *      girmeden önce hesabı açılmış TÜM mevcut kullanıcıları kalıcı olarak
 *      dışarıda bırakır — eski çalışanlar bu mesajı hiçbir zaman görmez.
 *   2. `Employees/{id}/loginHistory` alt koleksiyonunda en fazla 1 kayıt
 *      olmalı. LoginPage her girişte buraya bir kayıt yazdığı için, kayıt
 *      sayısının 1 olması "bu, kullanıcının ilk girişi" demektir. Sunucu
 *      tarafında tutulduğundan cihaz değişse de doğru çalışır.
 *   3. Bu tarayıcıda daha önce gösterilmemiş olmalı (localStorage). Aynı
 *      oturumda sayfa yenilendiğinde mesajın tekrar açılmasını engeller.
 */

// Bu özelliğin devreye alındığı tarih. Bu tarihten ÖNCE oluşturulmuş hiçbir
// hesap "yeni kullanıcı" sayılmaz. Değiştirmeyiniz — geçmişe dönük olarak
// mevcut kullanıcılara karşılama mesajı gösterilmesine yol açar.
const ACCOUNT_CUTOFF = new Date('2026-08-12T00:00:00Z');

// Girişi yapılmış kullanıcı için localStorage anahtarı (uid bazlı, böylece
// aynı bilgisayarı paylaşan kullanıcılar birbirini etkilemez).
const seenKey = (uid) => `habcare.welcomeSeen.${uid}`;

/** Karşılama mesajının bu tarayıcıda daha önce gösterilip gösterilmediği. */
export const hasSeenWelcome = (uid) => {
  if (!uid) return true;
  try {
    return localStorage.getItem(seenKey(uid)) === '1';
  } catch (err) {
    // localStorage erişilemiyorsa (gizli mod vb.) mesajı gösterme.
    return true;
  }
};

/** Karşılama mesajını "gösterildi" olarak işaretler. */
export const markWelcomeSeen = (uid) => {
  if (!uid) return;
  try {
    localStorage.setItem(seenKey(uid), '1');
  } catch (err) {
    console.error('Karşılama mesajı durumu kaydedilemedi:', err);
  }
};

/**
 * Kullanıcının ilk kez giriş yapan yeni bir çalışan olup olmadığını belirler.
 *
 * @param {string} employeeId Employees koleksiyonundaki doküman kimliği
 * @returns {Promise<boolean>}
 */
export const shouldShowWelcome = async (employeeId) => {
  const user = auth.currentUser;
  if (!user || !employeeId) return false;

  // 1) Hesap, özellik kesim tarihinden sonra mı açılmış?
  const creationTime = user.metadata?.creationTime;
  if (!creationTime) return false;
  const createdAt = new Date(creationTime);
  if (Number.isNaN(createdAt.getTime()) || createdAt < ACCOUNT_CUTOFF) return false;

  // 2) Bu tarayıcıda daha önce gösterildi mi? (ucuz kontrol, okuma yapmadan önce)
  if (hasSeenWelcome(user.uid)) return false;

  // 3) Kullanıcının kaç giriş kaydı var? (ilk girişte tam olarak 1 olur)
  try {
    const historyQuery = query(
      collection(db, 'Employees', employeeId, 'loginHistory'),
      limit(2)
    );
    const snapshot = await getDocs(historyQuery);
    return snapshot.size <= 1;
  } catch (err) {
    console.error('Giriş geçmişi okunamadı:', err);
    // Emin olamıyorsak mesajı gösterme — yanlış kullanıcıya göstermektense
    // hiç göstermemek daha güvenli.
    return false;
  }
};
